import React, { Children, memo, useEffect } from 'react';
import {
  Input,
  DatePicker,
  Switch,
  InputNumber,
  InputPercent,
  InputPrice,
  Radio,
  Checkbox,
  Tag,
} from '@bedrock/components';
import { ProTable } from '@bedrock/eed-react';
import classNames from 'classnames';
import PureText from './PureText';
import './index.less';
import TextAreaPopover from '../SForm/SFormElement/TextAreaPropover';
import TextAreaWithUpload from '#components/SForm/SFormElement/TextAreaWithUpload';
import CheckboxWithTextArea from '#components/SForm/SFormElement/CheckboxWithTextArea';
import InputNumberWithTopSlot from '#components/SForm/SFormElement/InputNumberWithTopSlot';
import SInputPercent from '#components/SForm/SFormElement/InputPercent';
import SingleCheckbox from '#components/SForm/SFormElement/SingleCheckbox';
import CurrencyInput from '#components/SForm/SFormElement/CurrencyInput';
import { SUpload } from '#components/SForm/SFormElement/SUpload';
import SFormItem from '../SForm/SFormItem';

function valueToArray(value: any) {
  if (Array.isArray(value)) return value;
  return [value];
}

const defaultProps = {
  className: 'mpc-quote-filed',
};

const TextAreaPopoverInput = (props) => {
  return (
    <TextAreaPopover
      renderChildren={(value) => {
        return <Input value={value} size="small" {...props.inputProps} />;
      }}
      {...props}
      view={false}
    />
  );
};

/**
 * 单元格编辑组件高阶函数 —— 为 ProTable 的 editRender 场景定制的 React.memo。
 *
 *## 背景
 * ProTable 在可编辑模式下，行数据变更、选中态变更、排序/筛选等都会触发列配置重建。
 * 每次 editRender 都会生成新的 props 对象（包括 inline 箭头函数形式的 onChange），
 * 若使用默认 React.memo 的浅比较，所有单元格都会因函数 identity 变化被重渲染，
 * 在大表格下性能损耗明显。
 *
 * ## 做了什么
 * 1. 统一给被包装组件注入 `size="small"`（项目默认编辑态尺寸）。
 * 2. 提供一个定制的 props 比较函数，按三类 prop 分流：
 *    - **受控 prop**（value / checked / defaultValue / defaultChecked）
 *      → 引用比较，不等即重渲染。这是受控组件能正确反映值变化的关键。
 *    - **渲染型函数 prop**（见 {@link RENDER_PROP_KEYS}，如 customComp）
 *      → 引用比较，不等即重渲染。这类 prop 本质是"渲染来源"而非事件回调，
 *        上层往往每次 editRender 都生成新箭头函数并在闭包里捕获最新 row/editable，
 *        若按普通函数 prop 跳过比较，会保留旧闭包 → 基于 row 的回显/交互用旧数据。
 *    - **事件回调型函数 prop**（onChange、onClick、onBlur 等其他函数）
 *      → 跳过比较。这是整个 memo 能省下渲染的核心点：屏蔽 inline 箭头函数
 *        identity 变化的噪音，让 onChange 每次重建都不触发重渲染。
 *    - **其他 prop** → 引用比较，不等即重渲染。
 *
 * ## 命中跳过渲染的条件（同时满足）
 * - 所有受控 prop 引用相等
 * - 所有渲染型函数 prop 引用相等
 * - 所有其他非函数 prop 引用相等
 * - prevProps 和 nextProps 的 key 数量相同
 *
 * ## 使用注意
 * - 若调用方新增一种"渲染来源"类的函数 prop（如自定义 render/formatter），
 *   应将其 key 加入 {@link RENDER_PROP_KEYS}，否则可能出现闭包回显 BUG。
 * - 事件回调跳过比较意味着内部使用的 handler 一定是上层每次 render 生成的最新版本
 *   （React 重用 fiber 时 props 会被替换），不会产生"用到旧 handler"的问题。
 * - 被包装组件内部的 useState / useEffect 不会因为跳过渲染而丢失，React 复用同一 fiber。
 * - 对于 `custom-component` 渲染器：
 *   - `customComp` 若写为 inline 箭头函数（闭包捕获 row），每次 editRender 必定新建，
 *     会触发重渲染 —— 这是业务最常用的方式，行为正确。
 *   - `customComp` 若写为模块顶层组件（identity 稳定），则依赖 props 里显式传入的
 *     `row` / `detail` 等通过"其他 prop 引用比较"分支触发重渲染。
 *   - 业务方若希望减少不必要重渲染，可用 `useMemoizedFn` 包装 `customComp`
 *     —— identity 保持稳定，但函数内部引用始终是最新值。但此时必须显式把依赖的
 *     `row` 等放到 props 里，否则闭包里的 row 永远是第一次的。
 *
 * @template P - 组件 props 类型
 * @param Component - 需要包装的受控组件
 * @returns 带有自定义比较函数的 memo 组件
 *
 * @example
 * ```tsx
 * // 受控组件：值变化时重渲染，onChange 引用变化被忽略
 * const CellTextArea = cellEditMemo(Input.TextArea);
 *
 * // 自定义渲染：customComp identity 变化时重渲染，保证闭包里的 row 是最新的
 * const CellMPCRender = cellEditMemo(({ customComp, ...rest }) => {
 *   const Comp = customComp;
 *   return <Comp {...rest} />;
 * });
 * ```
 */

/**
 * 渲染型函数 prop 白名单：即使为 function 也按引用比较（identity 变化必须重渲染）。
 *
 * Why: 这类 prop 本质是"渲染来源"，通常由上层在每次 editRender 中生成箭头函数，
 *      闭包里捕获了最新的 row / editable 等数据。一旦作为普通事件回调跳过比较，
 *      就会保留旧闭包 → 导致回显异常（如 customComp 里的 row 永远是首次的 row，
 *      score / result_score 列评分后点击编辑看到的明细是旧数据）。
 *
 * How to apply: 当通过 `ProTable.addEditRender` 新增一种渲染器，其 props 里
 *      暴露了"由调用方提供渲染函数"的字段（render / formatter / renderCell / customComp 等），
 *      将该字段的 key 加入此白名单。仅影响经过 cellEditMemo 包装的组件。
 */
const RENDER_PROP_KEYS = ['customComp'] as const;

function cellEditMemo<P extends Record<string, any>>(
  Component: React.ComponentType<P>
): React.MemoExoticComponent<React.ComponentType<P>> {
  // 注入默认 size='small'，统一编辑态单元格尺寸
  const LocalComponent: React.FC<P> = (props) => {
    return <Component size="small" {...props} />;
  };

  LocalComponent.displayName = `cellEditMemo(${Component.displayName || Component.name || 'Component'})`;

  return memo(LocalComponent, (prevProps, nextProps) => {
    // —— 阶段 1：受控 prop 引用比较（最高优先级，任意不等即重渲染） ——
    // value / checked 是受控组件表达"当前值"的核心 prop，必须如实反映变化。
    const controlledKeys = [
      'value',
      'checked',
      'defaultValue',
      'defaultChecked',
    ];

    for (const key of controlledKeys) {
      if (key in prevProps || key in nextProps) {
        if (prevProps[key] !== nextProps[key]) {
          return false;
        }
      }
    }

    // —— 阶段 2：props 数量快速判定 ——
    // key 数量不同意味着结构变化，直接重渲染，避免遍历遗漏新增/删除的 prop。
    const prevKeys = Object.keys(prevProps);
    const nextKeys = Object.keys(nextProps);

    if (prevKeys.length !== nextKeys.length) {
      return false;
    }

    // —— 阶段 3：逐 key 比较剩余 prop，按类型分流 ——
    for (const key of prevKeys) {
      // 受控 prop 已在阶段 1 处理，跳过
      if (controlledKeys.includes(key)) {
        continue;
      }

      // 渲染型函数 prop：虽为 function 但属于"渲染来源"，按引用比较
      // identity 变化一般意味着闭包捕获的上层数据已更新，必须重渲染才能正确反映
      if ((RENDER_PROP_KEYS as readonly string[]).includes(key)) {
        if (prevProps[key] !== nextProps[key]) {
          return false;
        }
        continue;
      }

      // 事件回调型函数 prop：跳过比较
      // 这是 memo 能省下渲染的核心点 —— 屏蔽 inline 箭头函数 identity 变化的噪音
      // React 在复用 fiber 时会替换 props，因此内部使用的 handler 永远是最新版本
      if (
        typeof prevProps[key] === 'function' &&
        typeof nextProps[key] === 'function'
      ) {
        continue;
      }

      // 其他 prop：引用比较
      if (prevProps[key] !== nextProps[key]) {
        return false;
      }
    }

    // 所有 prop 均判定为"未变化"，跳过重渲染
    return true;
  }) as React.MemoExoticComponent<React.ComponentType<P>>;
}

const CellTextArea = cellEditMemo(Input.TextArea);

const CellMPCInputNumber = cellEditMemo(InputNumber);

const CellInputPercent = cellEditMemo(InputPercent);

/**
 * 单元格版 InputPrice —— 适配层。
 *
 * 上层契约（业务方）:
 *   value: { value?: number | string, currency?: string }
 *   onChange(next: { value?: number | string, currency?: string })
 *
 * 底层契约（InputPrice）:
 *   value: string | number | undefined
 *   currency: string
 *   onChange(value)
 *   onCurrencyChange(currency)
 *
 * 本组件作为适配层，把上层"统一对象 + 单一 onChange"映射到底层"分裂 prop + 双 callback"。
 * InputPrice 任何事件都通过 { ...prev, [changedField]: newVal } 合并后整体抛出，
 * 杜绝上层收到"半更新"的中间状态。
 */
const CellInputPriceBase: React.FC<{
  value?: { value?: number | string; currency?: string };
  onChange?: (next: { value?: number | string; currency?: string }) => void;
  size?: 'small' | 'medium' | 'large';
  [key: string]: any;
}> = ({ value, onChange, ...rest }) => {
  const v = value?.value;
  const c = value?.currency;

  return (
    <InputPrice
      size="small"
      {...rest}
      value={v as any}
      currency={c}
      onChange={(next) => onChange?.({ value: next, currency: c })}
      onCurrencyChange={(next) => onChange?.({ value: v, currency: next })}
    />
  );
};

const CellInputPrice = cellEditMemo(CellInputPriceBase);

ProTable.addEditRender(
  'mpc-textArea',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => ({
    component: editable
      ? TextAreaPopoverInput
      : (props) => (
          <div
            onClick={() => {
              toggleEditableRowKey?.(
                getRowKey?.(props?.row) as string,
                editable
              );
            }}
          >
            <TextAreaPopover {...props} view="view" />
          </div>
        ),
    props: {
      ...props,
      onChange(e, row, column, rowIndex, ...args) {
        const changedRow = {
          ...row,
          [column.dataIndex as any]: e.target.value,
        };
        setRow(changedRow);
        props?.onChange?.(e, changedRow, column, rowIndex, ...args);
      },
    },
  })
);

const CellMPCRender = cellEditMemo(({ customComp: Comp, ...rest }) => {
  if (!Comp) return null;
  return <Comp {...rest} />;
});

ProTable.addEditRender(
  'custom-component',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => {
    return {
      component: CellMPCRender,
      props: {
        ...props,
        onChange(e, row, column, rowIndex, ...args) {
          const changedRow = {
            ...row,
            [column.dataIndex as any]: e.target.value,
          };
          setRow(changedRow);
          props?.onChange?.(e, changedRow, column, rowIndex, ...args);
        },
        setRow,
        editable,
      },
    };
  }
);

// ─── 一等渲染器：s-form-item ─────────────────────────────────────────────
// 单元格直接绑定外层 Form 的受控字段（SFormItem 内部经 Form.Item name= formItemKey 注册）。
// 与 custom-component 的本质差异：
//   - 编辑值走 Form.Item 受控契约自动写回外层 form 对应 key（getFieldsValue 可返回、提交可上送），
//     不依赖 setRow 写表格行数据，也不经过 onChange(e,row,column) 包装 —— 规避
//     「内联 customComp 每次重建 → 单元格子树卸载重挂载 → 输入抖动/有状态控件丢状态」与
//     「Select 裸值被 e.target.value 取失」两类高频坑，业务侧无需再为每个单元格手写顶层稳定组件。
// 列声明式用法：
//   editRender: () => ({
//     name: 's-form-item',
//     editable: true,
//     props: {
//       formItemKey: 'xxx',                 // 外层 form 字段 key
//       componentClass: 'select',
//       elementProps: { options, placeholder, className: 'w-full' },
//       className,                          // 追加到单元格 Form.Item（选传）
//       view,                               // 选传：精细覆盖只读态（缺省 = 表格级 readOnly 反值）
//     },
//   })
// 注：需要复用领域 FormModel 单一字段定义（options/show 谓词集中在模型）时，
// 业务可继续用共享的 FormModelCell 稳定组件（NegotiationTable/FormModelCell）；
// 简单字段绑定优先用本渲染器，写法最简。
const CellSFormItem = cellEditMemo((p: any) => {
  const { formItemKey, ...rest } = p;
  if (!formItemKey) return null;
  return (
    <SFormItem
      {...rest}
      formItemKey={formItemKey}
      showLabel={false}
      // 单元格内紧凑无间距 + 隐藏 label（对齐 BudgetOfferTable FORM_CELL_CLASS 范式）
      className={classNames('mb-0 [&_.rock-form-item-label]:hidden', rest.className)}
    />
  );
});

ProTable.addEditRender('s-form-item', ({ props }, editable) => ({
  component: CellSFormItem,
  // 表格级 editable（= ProTablePlusForm readOnly 反值）统一映射为字段 view；单元格可经 props.view 覆盖
  props: {
    ...(props || {}),
    view: (props as any)?.view ?? !editable,
    // SFormElement select 默认不撑满 → 常规单元格默认撑满，调用方可经 elementProps.className 覆盖
    elementProps: {
      ...((props as any)?.elementProps ?? {}),
      className: classNames('w-full', (props as any)?.elementProps?.className),
    },
  },
}));

ProTable.addEditRender(
  'switch-quote',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => {
    return {
      component: editable
        ? Switch
        : (props) => (
            <div
              onClick={() => {
                toggleEditableRowKey?.(
                  getRowKey?.(props.row) as string,
                  editable
                );
              }}
            >
              <Switch {...props} disabled />
            </div>
          ),
      props: {
        ...props,
        onChange(e, row, column, rowIndex, ...args) {
          const changedRow = {
            ...row,
            [column.dataIndex as any]: e.target.value,
          };
          setRow(changedRow);
          props?.onChange?.(e, changedRow, column, rowIndex, ...args);
        },
      },
    };
  }
);

ProTable.addEditRender(
  'single-radio',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => {
    return {
      component: editable
        ? ({ children, value, ...rest }) => {
            return (
              <Radio checked={value} {...rest}>
                {props.children}
              </Radio>
            );
          }
        : ({ children, value, ...rest }) => (
            <div
              onClick={() => {
                toggleEditableRowKey?.(
                  getRowKey?.(props.row) as string,
                  editable
                );
              }}
            >
              <Radio checked={value} {...rest} disabled>
                {props.children}
              </Radio>
            </div>
          ),
      props: {
        ...props,
        onChange(e, row, column, rowIndex, ...args) {
          const changedRow = {
            ...row,
            [column.dataIndex as any]: e.target.checked,
          };
          setRow(changedRow);
          props?.onChange?.(e, changedRow, column, rowIndex, ...args);
        },
      },
    };
  }
);

ProTable.addEditRender(
  'single-checkbox',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => {
    return {
      component: SingleCheckbox,
      props: {
        ...props,
        onChange(e, row, column, rowIndex, ...args) {
          const changedRow = {
            ...row,
            [column.dataIndex as any]: e.target.value,
          };
          setRow(changedRow);
          props?.onChange?.(e, changedRow, column, rowIndex, ...args);
        },
      },
    };
  }
);

ProTable.addEditRender(
  'text_with_upload',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => {
    return {
      component: TextAreaWithUpload,
      props: {
        ...props,
        view: !editable,
        onChange(e, row, column, rowIndex, ...args) {
          const changedRow = {
            ...row,
            [column.dataIndex as any]: e.target.value,
          };
          setRow(changedRow);
          props?.onChange?.(e, changedRow, column, rowIndex, ...args);
        },
      },
    };
  }
);

ProTable.addEditRender(
  'inputTextarea',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => ({
    component: editable
      ? CellTextArea
      : (props) => (
          <div
            onClick={() => {
              toggleEditableRowKey?.(getRowKey?.(props?.row) as string, true);
            }}
          >
            <PureText {...props} />
          </div>
        ),
    props: {
      ...props,
      onChange(e, row, column, rowIndex, ...args) {
        const changedRow = {
          ...row,
          [column.dataIndex as any]: e.target.value,
        };
        setRow(changedRow);
        props?.onChange?.(e, changedRow, column, rowIndex, ...args);
      },
    },
  })
);

ProTable.addEditRender(
  'MPCInputNumber',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => ({
    component: editable
      ? CellMPCInputNumber
      : (props) => (
          <div
            onClick={() => {
              toggleEditableRowKey?.(getRowKey?.(props?.row) as string, true);
            }}
          >
            <PureText {...props} />
          </div>
        ),
    props: {
      ...props,
      onChange(e, row, column, rowIndex, ...args) {
        const changedRow = {
          ...row,
          [column.dataIndex as any]: e.target.value,
        };
        setRow(changedRow);
        props?.onChange?.(e, changedRow, column, rowIndex, ...args);
      },
    },
  })
);

ProTable.addEditRender(
  'inputPercent',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => ({
    component: editable
      ? CellInputPercent
      : (props) => (
          <div
            onClick={() => {
              toggleEditableRowKey?.(getRowKey?.(props?.row) as string, true);
            }}
          >
            <PureText {...props} />
          </div>
        ),
    props: {
      ...props,
      direction: 'vertical',
      onChange(e, row, column, rowIndex, ...args) {
        const v = e?.target?.value;
        // SInputPercent 的 onChange 直接传值（非 event 对象）
        const changedRow = {
          ...row,
          [column.dataIndex as any]: v,
        };
        setRow(changedRow);
        props?.onChange?.(v, changedRow, column, rowIndex, ...args);
      },
    },
  })
);

ProTable.addEditRender(
  'MPCInputPrice',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => ({
    component: editable
      ? CellInputPrice
      : (props) => (
          <div
            onClick={() => {
              toggleEditableRowKey?.(getRowKey?.(props?.row) as string, true);
            }}
          >
            <PureText {...props} />
          </div>
        ),
    props: {
      ...props,
      // 上层 props.value 已是 {value, currency}（来自 useDetailData.format），
      // CellInputPrice 接收此对象并通过单一 onChange 抛回完整对象。
      // 注：ProTable 框架会把 cell 的 onChange(<arg>) 自动包装为 { target: { value: <arg> } }
      //     以匹配 addEditRender 的 ChangeEvent 签名，需 e?.target?.value 解包后才能拿到真实值。
      onChange(e, row, column, rowIndex, ...args) {
        const next = e?.target?.value;
        const changedRow = { ...row, [column.dataIndex]: next };
        setRow(changedRow);
        props?.onChange?.(next, changedRow, column, rowIndex, ...args);
      },
    },
  })
);

// @ts-ignore
ProTable?.addEditRender?.(
  'budgetInputNumber',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => ({
    component: InputNumberWithTopSlot,
    props: {
      ...props,
      onChange(e, row, column, rowIndex, ...args) {
        const changedRow = {
          ...row,
          [column.dataIndex as any]: e.target.value,
        };
        setRow(changedRow);
        props?.onChange?.(e, changedRow, column, rowIndex, ...args);
      },
    },
  })
);

// @ts-ignore
ProTable?.addBatchEditRender?.('inputNumber', ({ props }) => ({
  component: InputNumber,
  props: {
    ...props,
    onChange(e, column, rowIndex, ...args) {
      props?.onChange?.(e, column, rowIndex, ...args);
    },
  },
}));

// @ts-ignore
ProTable?.addBatchEditRender?.('inputPercent', ({ props }) => ({
  component: InputPercent,
  props: {
    ...props,
    onChange(e, column, rowIndex, ...args) {
      props?.onChange?.(e?.target?.value, column, rowIndex, ...args);
    },
  },
}));

ProTable?.addBatchEditRender?.('dateRange', ({ props }) => ({
  component: DatePicker,
  props: {
    range: true,
    ...props,
    onChange(e, column, rowIndex, ...args) {
      props?.onChange?.(e, column, rowIndex, ...args);
    },
  },
}));

ProTable.addEditRender(
  'radio-group',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => {
    return {
      component: Radio.Group,
      props: {
        ...props,
        onChange(e, row, column, rowIndex, ...args) {
          const changedRow = {
            ...row,
            [column.dataIndex as any]: e.target.value,
          };
          setRow(changedRow);
          props?.onChange?.(e, changedRow, column, rowIndex, ...args);
        },
        className: `${props.className} mpc-pro-table-radio-group`,
      },
    };
  }
);

ProTable.addEditRender(
  'checkbox-with-text-area',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => {
    return {
      component: CheckboxWithTextArea,
      props: {
        ...props,
        onChange(e, row, column, rowIndex, ...args) {
          const changedRow = {
            ...row,
            [column.dataIndex as any]: e.target.value,
          };
          setRow(changedRow);
          props?.onChange?.(e, changedRow, column, rowIndex, ...args);
        },
      },
    };
  }
);

/**
 * 单元格版金额+币种（CurrencyInput）—— 与 SForm componentClass="currencyInput" 共用同一组件。
 *
 * 值契约：{ value, currency } 对象。
 * - 编辑态：直接渲染 CurrencyInput（受控，onChange 抛完整对象）。
 * - 只读态：渲染 CurrencyInput view（支持 unit 气泡 / color / currencyBeforeAmount 排布），点击进入编辑。
 *
 * 注：ProTable 框架会把 cell 的 onChange(<arg>) 自动包装为 { target: { value: <arg> } }
 *     以匹配 addEditRender 的 ChangeEvent 签名，需 e?.target?.value 解包后才能拿到真实对象。
 */
const CellCurrencyInput = cellEditMemo(CurrencyInput);

ProTable.addEditRender(
  'currencyInput',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => ({
    component: editable
      ? CellCurrencyInput
      : (renderProps) => (
          <div
            onClick={() => {
              toggleEditableRowKey?.(
                getRowKey?.(renderProps?.row) as string,
                true
              );
            }}
          >
            <CurrencyInput view {...renderProps} />
          </div>
        ),
    props: {
      ...props,
      onChange(e, row, column, rowIndex, ...args) {
        const next = e?.target?.value;
        const changedRow = { ...row, [column.dataIndex]: next };
        setRow(changedRow);
        props?.onChange?.(next, changedRow, column, rowIndex, ...args);
      },
    },
  })
);

/**
 * 单元格版文件上传（SUpload）—— 与 SForm componentClass="upload" 共用同一组件。
 *
 * 值契约：AttachmentDots[]（fileList 数组）。
 * - 编辑态：直接渲染 SUpload（受控 fileList，onChange 抛完整 dataList）。
 * - 只读态：view=true 渲染只读文件列表（隐藏上传按钮），空附件不渲染
 *   （对齐 SFormElement index.tsx upload 空值处理）。
 *
 * 注：
 * - ProTable 框架会把 cell 的 onChange(<arg>) 自动包装为 { target: { value: <arg> } }
 *   以匹配 addEditRender 的 ChangeEvent 签名。SUpload 的 onChange 对象无 target 属性，
 *   e?.target?.value 解包后即 { dataList, ...UploadChangeParam }，dataList 才是要存的单元格值。
 * - cellEditMemo 统一注入 size="small"，但 bedrock Upload 的 size 仅支持 'large' | 'default'，
 *   包装器内解构剥离，避免类型冲突与布局异常。
 */
const CellUpload = ({ value, view, size, ...rest }) => {
  const fileList = value || [];

  if (view && !fileList.length) {
    return null;
  }
  return <SUpload fileList={fileList} view={view} {...rest} />;
};

ProTable.addEditRender('upload', ({ props, setRow }, editable) => ({
  component: CellUpload,
  props: {
    ...props,
    view: !editable,
    onChange(e, row, column, rowIndex, ...args) {
      const next = e?.target?.value;
      const nextList = next?.dataList;
      const changedRow = { ...row, [column.dataIndex]: nextList };
      setRow(changedRow);
      props?.onChange?.(nextList, changedRow, column, rowIndex, ...args);
    },
  },
}));
